import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Search,
  Plus,
  Filter,
  PillBottle,
  AlertTriangle,
  ChevronRight,
  CalendarOff,
  PackageX,
  Clock4,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { Badge } from '@/components/common/Badge'
import { EmptyState, Loader, SkeletonRow } from '@/components/common/Loader'
import { useDrugStore } from '@/store/drugStore'
import {
  DRUG_CATEGORY_OPTIONS,
  DRUG_CATEGORY_LABEL,
  STOCK_FILTER_TABS,
} from '@/lib/constants'
import { cn, formatRupees, formatDate, daysFromToday } from '@/lib/utils'

function StockStatePills({ drug }) {
  const pills = []
  if (drug.is_expired) {
    pills.push({ tone: 'danger', icon: <CalendarOff className="h-3 w-3" />, label: 'Expired' })
  } else if (drug.is_near_expiry) {
    pills.push({
      tone: 'accent',
      icon: <Clock4 className="h-3 w-3" />,
      label: `${drug.days_to_expiry}d to expiry`,
    })
  }
  if (drug.stock_count === 0) {
    pills.push({ tone: 'danger', icon: <PackageX className="h-3 w-3" />, label: 'Out of stock' })
  } else if (drug.is_low_stock) {
    pills.push({ tone: 'accent', icon: <AlertTriangle className="h-3 w-3" />, label: 'Low stock' })
  }
  if (drug.is_slow_moving) {
    pills.push({ tone: 'neutral', label: 'Slow moving' })
  }
  if (drug.is_active === false) {
    pills.push({ tone: 'muted', label: 'Inactive' })
  }
  if (pills.length === 0) {
    pills.push({ tone: 'success', label: 'Healthy' })
  }
  return (
    <div className="flex flex-wrap gap-1">
      {pills.map((p, i) => (
        <Badge key={i} tone={p.tone} size="xs" leftIcon={p.icon}>
          {p.label}
        </Badge>
      ))}
    </div>
  )
}

export default function DrugList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { list, loading, hasMore, loadingMore, fetchList, nextPage, query } = useDrugStore()

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const activeFilter = searchParams.get('filter') || 'all'
  const activeCategory = searchParams.get('category') || ''

  // Sync URL → store on mount and when URL changes
  useEffect(() => {
    fetchList({
      search: searchParams.get('search') || '',
      category: searchParams.get('category') || '',
      filter: searchParams.get('filter') || '',
    }).catch(() => toast.error('Could not load drugs'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(patch).forEach(([k, v]) => {
      if (!v) next.delete(k)
      else next.set(k, v)
    })
    setSearchParams(next, { replace: true })
  }

  const handleSearch = (e) => {
    e.preventDefault()
    updateParams({ search: searchInput.trim() })
  }

  return (
    <div className="container-page py-6 sm:py-8">
      <PageHeader
        eyebrow="Phase 6 — Inventory"
        title="Drug list"
        description="Every medicine in the clinic stock. Filters help you spot expiring, low-stock and slow-moving items at a glance."
        actions={
          <Button
            onClick={() => navigate('/drugs/new')}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add medicine
          </Button>
        }
      />

      {/* Filters */}
      <div className="mt-6 space-y-4">
        {/* Search + category */}
        <form
          onSubmit={handleSearch}
          className="grid gap-3 rounded-2xl bg-white p-3 shadow-soft ring-1 ring-ink-100 sm:grid-cols-[1fr_220px_auto]"
        >
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by medicine name or ID (RD000DR…)"
            leftIcon={<Search className="h-4 w-4" />}
          />
          <select
            className="h-11 rounded-xl bg-white px-3 text-sm font-medium text-ink-800 ring-1 ring-inset ring-ink-200 transition focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={activeCategory}
            onChange={(e) => updateParams({ category: e.target.value })}
          >
            <option value="">All categories</option>
            {DRUG_CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <Button type="submit" variant="secondary" leftIcon={<Filter className="h-4 w-4" />}>
            Apply
          </Button>
        </form>

        {/* Stock filter chips */}
        <div className="flex flex-wrap gap-1.5">
          {STOCK_FILTER_TABS.map((tab) => {
            const isActive = activeFilter === tab.value
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => updateParams({ filter: tab.value === 'all' ? '' : tab.value })}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-xs font-semibold transition ring-1 ring-inset',
                  isActive
                    ? 'bg-primary-700 text-cream ring-primary-700 shadow-soft'
                    : 'bg-white text-ink-700 ring-ink-200 hover:bg-ink-50 hover:ring-ink-300',
                )}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Results */}
      <div className="mt-6">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} className="h-16" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-2xl bg-white shadow-soft ring-1 ring-ink-100">
            <EmptyState
              icon={<PillBottle className="h-6 w-6" />}
              title="No medicines found"
              description={
                query.search || query.category || query.filter
                  ? 'Try clearing filters or a different search term.'
                  : 'Add your first medicine to start tracking stock.'
              }
              action={
                <Button onClick={() => navigate('/drugs/new')} leftIcon={<Plus className="h-4 w-4" />}>
                  Add medicine
                </Button>
              }
            />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-2xl bg-white shadow-soft ring-1 ring-ink-100 lg:block">
              <table className="w-full text-sm">
                <thead className="bg-ink-50/60 text-left">
                  <tr>
                    <th className="px-4 py-3 label-text">Medicine</th>
                    <th className="px-4 py-3 label-text">Category</th>
                    <th className="px-4 py-3 label-text">Stock</th>
                    <th className="px-4 py-3 label-text">MRP</th>
                    <th className="px-4 py-3 label-text">Expiry</th>
                    <th className="px-4 py-3 label-text">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((drug) => {
                    const d2e = daysFromToday(drug.exp_date)
                    return (
                      <tr
                        key={drug.drug_id}
                        className="cursor-pointer border-t border-ink-100 transition hover:bg-cream/60"
                        onClick={() => navigate(`/drugs/${drug.drug_id}`)}
                      >
                        <td className="px-4 py-3">
                          <p className="font-semibold text-ink-900">{drug.name}</p>
                          <p className="text-[11px] font-mono text-ink-500">{drug.drug_id}</p>
                          {drug.dosage && (
                            <p className="text-xs text-ink-500">{drug.dosage}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ink-700">
                          {DRUG_CATEGORY_LABEL[drug.category_id] || drug.category_id}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'font-bold tabular-nums',
                              drug.stock_count === 0
                                ? 'text-rose-600'
                                : drug.is_low_stock
                                ? 'text-accent-700'
                                : 'text-ink-900',
                            )}
                          >
                            {drug.stock_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 tabular-nums text-ink-700">
                          {formatRupees(drug.mrp)}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-ink-700">{formatDate(drug.exp_date)}</p>
                          {d2e !== null && (
                            <p
                              className={cn(
                                'text-[11px]',
                                d2e < 0
                                  ? 'text-rose-600 font-semibold'
                                  : d2e < 90
                                  ? 'text-accent-700'
                                  : 'text-ink-500',
                              )}
                            >
                              {d2e < 0 ? `${Math.abs(d2e)}d ago` : `in ${d2e}d`}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StockStatePills drug={drug} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <ChevronRight className="ml-auto h-4 w-4 text-ink-400" />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile / tablet cards */}
            <div className="space-y-2 lg:hidden">
              {list.map((drug) => (
                <Link
                  to={`/drugs/${drug.drug_id}`}
                  key={drug.drug_id}
                  className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-soft ring-1 ring-ink-100 transition hover:bg-cream/60"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                    <PillBottle className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-ink-900">{drug.name}</p>
                        <p className="text-[11px] font-mono text-ink-500">{drug.drug_id}</p>
                      </div>
                      <p
                        className={cn(
                          'shrink-0 text-right text-lg font-bold tabular-nums',
                          drug.stock_count === 0
                            ? 'text-rose-600'
                            : drug.is_low_stock
                            ? 'text-accent-700'
                            : 'text-ink-900',
                        )}
                      >
                        {drug.stock_count}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-ink-500">
                      {DRUG_CATEGORY_LABEL[drug.category_id] || drug.category_id} ·{' '}
                      {formatRupees(drug.mrp)} · Exp {formatDate(drug.exp_date)}
                    </p>
                    <div className="mt-2">
                      <StockStatePills drug={drug} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => nextPage().catch(() => toast.error('Could not load more'))}
                  loading={loadingMore}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
